import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, generatePath, Switch, Route } from 'react-router-dom';
import { matchPath, useHistory, useLocation, useRouteMatch } from 'react-router';
import Bluebird from 'bluebird';
import get from 'lodash/get';
import { Typography, Grid, FormControl, CardContent, Box, Breadcrumbs } from '@mui/material';
import routes from '../routes';
import { fetchReceiversIfNeeded, updateReceiverLocation, updateReceiverOperations } from '../actions/receivers';
import ReceiverStaffUsersTable from '../components/ReceiverStaffUsersTable';
import ReceiverLocationForm from '../components/ReceiverLocationForm';
import * as receiversActions from '../actions/receivers';
import * as foodDonorsActions from '../actions/foodDonors';
import * as rescuersActions from '../actions/rescuers';
import * as aggregatesActions from '../actions/aggregates';
import OverlayLoader from '../components/OverlayLoader';
import CompactWidgetTable from '../components/CompactWidgetTable';
import { getTopReceiverDonorsTableData, getTopReceiverRescuersTableData } from '../helpers/getters';
import * as notificationsHelper from '../helpers/notifications';
import NotificationsContext from '../context/NotificationsContext/NotificationsContext';
import * as authService from '../services/auth';
import { Roles } from '../models/roles';
import { BaseCard, BaseCardHeader } from '../components/Common/BaseCard';
import DateAndGranularityPicker, {
  dateRangeOptionsList,
} from '../components/DateAndGranularityPicker/DateAndGranularityPicker';
import PastRescuesListView from './PastRescuesListView';
import FutureRescuesListView from './FutureRescuesListView';
import { fetchFoodDescriptionsIfNeeded } from '../actions/foodDescriptions';
import ReceiverOperationsForm from '../components/ReceiverOperationsForm';
import { Colors } from '../assets/theme/Colors';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { hideReceiverMissingInfoPopup } from '../actions/ui';
import { fetchReceivingAgencyLogs } from '../actions/logs';
import ItemLogsTable from '../components/ItemLogsTable';

const TAB_FUTURE_RESCUES = 'future-rescues';
const TAB_PAST_RESCUES = 'past-rescues';
const TAB_STAFF = 'staff';
export const TAB_OPERATIONS = 'operations';
export const TAB_PROFILE = 'profile';
const TAB_LEADERBOARDS = 'leaderboards';
const TAB_PROFILE_NEEDS = 'needs-profile';
const TAB_LOGS = 'logs';

export const dashboardPaths = [
  generatePath(routes.dashboardReceiver),
  generatePath(routes.dashboardReceiver, { tab: TAB_PROFILE }),
  generatePath(routes.dashboardReceiver, { tab: TAB_OPERATIONS }),
  generatePath(routes.dashboardReceiver, { tab: TAB_STAFF }),
  generatePath(routes.dashboardReceiver, { tab: TAB_FUTURE_RESCUES }),
  generatePath(routes.dashboardReceiver, { tab: TAB_PAST_RESCUES }),
  generatePath(routes.dashboardReceiver, { tab: TAB_LEADERBOARDS }),
  generatePath(routes.dashboardReceiver, { tab: TAB_PROFILE_NEEDS }),
];

export const locationPaths = (id) => [
  generatePath(routes.receiver, { id }),
  generatePath(routes.receiver, { id, tab: TAB_PROFILE }),
  generatePath(routes.receiver, { id, tab: TAB_OPERATIONS }),
  generatePath(routes.receiver, { id, tab: TAB_STAFF }),
  generatePath(routes.receiver, { id, tab: TAB_FUTURE_RESCUES }),
  generatePath(routes.receiver, { id, tab: TAB_PAST_RESCUES }),
  generatePath(routes.receiver, { id, tab: TAB_LEADERBOARDS }),
  generatePath(routes.receiver, { id, tab: TAB_PROFILE_NEEDS }),
  generatePath(routes.receiver, { id, tab: TAB_LOGS }),
];

const linkSx = {
  textDecoration: 'none',
  color: Colors.gray,
  paddingLeft: '10px',
  '&.active': {
    color: 'primary.main',
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      left: -3,
      top: 0,
      height: '100%',
      borderLeft: '4px solid',
      borderColor: 'primary.main',
    },
  },
};

const LeaderboardsTab = ({ receiverId, fetchReceiverLocationDashboardAggregates }) => {
  const rescuers = useSelector((state) => state.entities.rescuers);
  const aggregates = useSelector((state) => state.aggregates);
  const foodDonors = useSelector((state) => state.entities.foodDonors);

  const showLoading = aggregates.rescues.inflight > 0;
  const LBS_PER_MEAL = 1.2;

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Box mb={2}>
            <Typography variant="h6" color="textSecondary">
              Leaderboards
            </Typography>
          </Box>
        </Grid>
        <Grid item>
          <Box boxShadow={25} p={2} my={1} bgcolor="background.paper">
            <FormControl component="fieldset">
              <DateAndGranularityPicker
                defaultDateRange={dateRangeOptionsList.thisQTD.value}
                dateRangeOptions={{
                  lastWeek: dateRangeOptionsList.lastWeek,
                  last7Days: dateRangeOptionsList.last7Days,
                  last90Days: dateRangeOptionsList.last90Days,
                  today: dateRangeOptionsList.today,
                  thisMTD: dateRangeOptionsList.thisMTD,
                  thisQTD: dateRangeOptionsList.thisQTD,
                  thisYTD: dateRangeOptionsList.thisYTD,
                }}
                customDateRangeOptions={{
                  maxDate: new Date(),
                }}
                showAllDateRange
                showCustomDateRange
                showGranularity={false}
                isDisabled={showLoading}
                onChange={({ dateRange }) => fetchReceiverLocationDashboardAggregates(dateRange)}
              />
            </FormControl>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <OverlayLoader
            isLoading={foodDonors.inflight || get(aggregates, "rescues.count['receiver,donor_location'].inflight", true)}
          >
            <BaseCard>
              <BaseCardHeader title="Top Food Donors" />
              <CardContent>
                <CompactWidgetTable
                  data={getTopReceiverDonorsTableData(
                    {
                      receiverId,
                      foodDonors,
                      aggregates,
                    },
                    LBS_PER_MEAL
                  )}
                  columns={[
                    {
                      name: 'name',
                      label: 'Name',
                      options: {
                        filter: false,
                      },
                    },
                    {
                      name: 'rescues',
                      label: '#Rescues',
                      options: {
                        filter: false,
                      },
                    },
                    {
                      name: 'meals',
                      label: '#Meals',
                      options: {
                        filter: false,
                      },
                    },
                    {
                      name: 'lbs',
                      label: '#lbs',
                      options: {
                        filter: false,
                      },
                    },
                  ]}
                />
              </CardContent>
            </BaseCard>
          </OverlayLoader>
        </Grid>

        <Grid item xs={12} sm={6}>
          <OverlayLoader
            isLoading={rescuers.inflight || get(aggregates, "rescues.count['receiver,rescuer'].inflight", true)}
          >
            <BaseCard>
              <BaseCardHeader title="Top Rescuers" />
              <CardContent>
                <CompactWidgetTable
                  data={getTopReceiverRescuersTableData(
                    {
                      receiverId,
                      rescuers,
                      aggregates,
                    },
                    LBS_PER_MEAL
                  )}
                  columns={[
                    {
                      name: 'name',
                      label: 'Name',
                      options: {
                        filter: false,
                      },
                    },
                    {
                      name: 'rescues',
                      label: '#Rescues',
                      options: {
                        filter: false,
                      },
                    },
                    {
                      name: 'meals',
                      label: '#Meals',
                      options: {
                        filter: false,
                      },
                    },
                    {
                      name: 'lbs',
                      label: '#lbs',
                      options: {
                        filter: false,
                      },
                    },
                  ]}
                />
              </CardContent>
            </BaseCard>
          </OverlayLoader>
        </Grid>
      </Grid>
    </>
  );
};

const StaffTab = ({ receiver, receiverLocation, receivers, deleteReceiverStaff, notificationsContext }) => {
  const receiverLocationStaff = receivers.locations.staff.byLocationId[receiverLocation.id];

  const onReceiverLocationStaffDelete = user => Bluebird
    .try(() => deleteReceiverStaff(
      receiver.id,
      receiverLocation.id,
      user.id,
      user.is_admin
    ))
    .then(() => notificationsHelper.addNotification('Receiving Agency staff deleted successfully!', notificationsContext));

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box mb={2}>
          <Typography variant="h6" color="textSecondary">
            Staff
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <ReceiverStaffUsersTable
          tableId="#receiver-dashboard/receiver-staff-users"
          receiverId={receiver.id}
          receiverLocationStaff={receiverLocationStaff}
          showDeleteReceiverLocationStaffButton={
            authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
              Roles.Admin,
              Roles.NationalSiteDirector,
              Roles.SiteDirector,
            ])
          }
          onReceiverLocationStaffDelete={onReceiverLocationStaffDelete}
        />
      </Grid>
    </Grid>
  );
};

const LocationOperationsTab = ({
  receiver,
  receivers,
  receiverLocation,
  foodDescriptions,
  handleUpdateReceiverOperations,
}) => {
  const location = useLocation();
  const isLoading = receiver.inflight || receivers?.needsProfile.inflight || foodDescriptions.inflight;
  const { hash, state } = location;

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box mb={2}>
          <Typography variant="h6" color="textSecondary">
            Operations
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <OverlayLoader isLoading={isLoading}>
          <ReceiverOperationsForm
            missingInfo={state?.missingInfo || {}}
            receiverLocation={receiverLocation}
            hash={hash}
            submitHandler={(operationsData) =>
              handleUpdateReceiverOperations(receiver.id, receiverLocation.id, operationsData)
            }
          />
        </OverlayLoader>
      </Grid>
    </Grid>
  );
};

const LocationProfileTab = ({
  receiver,
  receiverTypes,
  receiverLocation,
  staff,
  handleUpdateReceiverLocation,
  handleUpdateReceiverLocationLogo,
  isAdmin,
}) => {
  const receiverTypeList = receiverTypes.allIds.map((id) => receiverTypes.byId[id]);
  const isLoading = receiver.inflight;

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box mb={2}>
          <Typography variant="h6" color="textSecondary">
            Profile
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <OverlayLoader isLoading={isLoading}>
          <ReceiverLocationForm
            showNotes={isAdmin}
            receiverTypes={receiverTypeList}
            location={receiverLocation}
            receiver={receiver}
            user={staff.byId[receiverLocation.primary_contact_id]}
            onSubmit={(location) => handleUpdateReceiverLocation(receiver.id, receiverLocation.id, location)}
            onLogoSave={(logoUrl) => handleUpdateReceiverLocationLogo(receiver.id, receiverLocation.id, logoUrl)}
          />
        </OverlayLoader>
      </Grid>
    </Grid>
  );
};

const FutureRescuesTab = ({ receiver, isAdmin }) => (
  <Grid container>
    <Grid item xs={12}>
      <Box mb={2}>
        <Typography variant="h6" color="textSecondary">
          Future Rescues
        </Typography>
      </Box>
    </Grid>
    <Grid item>
      <FutureRescuesListView
        filter={(rescue) => rescue.receiver_id === receiver.id}
        showCopyRescueButton={false}
        showEditRescueButton={isAdmin}
        displayFoodDonorFilters={false}
        renderNamesAsLinks={false}
        disableCancelAfterPickupDate
      />
    </Grid>
  </Grid>
);

const LogsTab = ({ receiver }) => {
  const dispatch = useDispatch();
  const logs = useSelector((state) => state.entities.logs);

  useEffect(() => {
    dispatch(fetchReceivingAgencyLogs(receiver.id));
  }, [receiver.id]);

  return (
    <ItemLogsTable
      tableId="#receiver-dashboard/receiver-logs"
      title="Receiving Agency Logs"
      logs={logs.byReceiverId[receiver.id]}
    />
  );
};

const PastRescuesTab = ({ receiver }) => (
  <Grid container>
    <Grid item xs={12}>
      <Box mb={2}>
        <Typography variant="h6" color="textSecondary">
          Past Rescues
        </Typography>
      </Box>
    </Grid>
    <Grid item>
      <PastRescuesListView
        filter={(rescue) => rescue.receiver_id === receiver.id}
        showCopyRescueButton={false}
        renderNamesAsLinks={false}
        filterColumns={['close_comments']}
        displayFoodDonorFilters={false}
        receiverName={receiver?.name}
      />
    </Grid>
  </Grid>
);

const MissingOperationsInfo = ({ hide, onClose, onComplete }) => {
  const buttons = [
    {
      label: 'Maybe Later',
    },
    {
      label: 'Complete Profile',
      color: 'primary',
      onClick: () => onComplete(),
    },
  ];

  const message = "Uh-oh! We're missing some information from you that will help us make sure we're sending you the food that works best for your agency.";

  const title = 'Information missing';

  if (hide) {
    return null;
  }

  return <ConfirmationDialog buttons={buttons} closeDialog={onClose} message={message} title={title} />;
};

const ReceiverDashboard = ({ renderParams }) => {
  const match = useRouteMatch();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();
  const notificationsContext = useContext(NotificationsContext);
  const receiverTypes = useSelector((state) => state.entities.receivers.types);
  const foodDescriptions = useSelector((state) => state.entities.foodDescriptions);
  const receivers = useSelector((state) => state.entities.receivers);
  const hideMissingOperationsInfo = useSelector((state) => state.ui.hideReceiverMissingInfoPopup);

  const staff = receivers.locations.staff;
  const receiverId = match.params.id || renderParams.id;
  const receiver = receivers.byId[receiverId] || {};
  const locations = receivers.locations.byReceiverId[receiverId] || [];
  const receiverLocation = locations[0] || {};

  const receiverNeedsProfileEntities = useSelector((state) => state.entities.receivers.needsProfile);
  const receiverNeedsProfile = receiverNeedsProfileEntities.byLocationId[receiverLocation?.id] || [];
  const needsAdded = receiverNeedsProfile.find((need) => need?.rating > 0);
  const locationHours = receiverLocation?.weekly_receiving_hours || [];
  const hoursAdded = locationHours.length > 0;
  const locationServedDaily = receiverLocation?.served_daily || '';
  const servedDailyAdded = Number(locationServedDaily) > 0;

  const isAdmin = authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
    Roles.Admin,
    Roles.NationalSiteDirector,
    Roles.SiteDirector,
    Roles.SiteCoordinator,
  ]);

  useEffect(() => {
    dispatch(fetchReceiversIfNeeded());
    dispatch(foodDonorsActions.fetchFoodDonorsIfNeeded());
    dispatch(rescuersActions.fetchRescuersIfNeeded());
    dispatch(receiversActions.fetchReceiverTypesIfNeeded());
    dispatch(fetchFoodDescriptionsIfNeeded());
  }, []);

  useEffect(() => {
    if (receiver.id && receiverLocation.id) {
      dispatch(receiversActions.fetchReceiverNeedsProfile(receiver.id, receiverLocation.id));
    }
  }, [receiver.id, receiverLocation.id]);

  const handleUpdateReceiverLocation = (receiverId, receiverLocationId, receiverLocation) => {
    dispatch(updateReceiverLocation(receiverId, receiverLocationId, receiverLocation));
  };

  const handleUpdateReceiverLocationLogo = (receiverId, receiverLocationId, logoUrl) =>
    dispatch(receiversActions.updateReceiverLocationLogo(receiverId, receiverLocationId, logoUrl));

  const handleUpdateReceiverOperations = (receiverId, receiverLocationId, receiverLocation) => {
    dispatch(updateReceiverOperations(receiverId, receiverLocationId, receiverLocation));
  };

  const fetchReceiverLocationDashboardAggregates = (dateRange, granularity) => {
    dispatch(aggregatesActions.fetchReceiverLocationDashboardAggregatesIfNeeded(dateRange, granularity));
  };

  const deleteReceiverStaff = (receiverId, locationId, userId, isAdmin) => {
    dispatch(receiversActions.deleteReceiverStaff(receiverId, locationId, userId, isAdmin));
  };

  const isLocationView = matchPath(location.pathname, {
    path: routes.receiver,
    exact: true,
  });

  const renderBreadcrumbs = () => (
    <Breadcrumbs aria-label="Breadcrumbs">
      {isAdmin ? (
        <Link color="inherit" to={generatePath(routes.allUsers, { tab: 'receivers' })}>
          Receiving Agencies
        </Link>
      ) : (
        <Typography color="textPrimary">Receiving Agencies</Typography>
      )}

      <Typography color="textPrimary">{receiver?.name}</Typography>
    </Breadcrumbs>
  );

  const paths = isLocationView ? locationPaths(isLocationView.params.id) : dashboardPaths;
  const canViewLogs = isLocationView && isAdmin;

  const dashboardMenuList = [
    {
      name: 'Profile',
      path: paths[0],
    },
    {
      name: 'Operations',
      path: paths[2],
    },
    {
      name: 'Staff',
      path: paths[3],
    },
    {
      name: 'Future Rescues',
      path: paths[4],
    },
    {
      name: 'Past Rescues',
      path: paths[5],
    },
    {
      name: 'Leaderboards',
      path: paths[6],
    },
    ...(canViewLogs
      ? [
          {
            name: 'Logs',
            path: paths[8],
          },
        ]
      : []),
  ];

  const getActiveTab = () => {
    const menuPaths = dashboardMenuList.map((item) => item.path);
    const path = menuPaths.indexOf(url);

    return Math.max(path, 0);
  };

  const handleHideMissingInfo = () => dispatch(hideReceiverMissingInfoPopup());

  const handleCompleteProfile = () =>
    history.push(generatePath(routes.dashboardReceiver, { tab: TAB_OPERATIONS }), {
      missingInfo: { hours: !hoursAdded, needs: !needsAdded, servedDaily: !servedDailyAdded },
    });

  if (receiver.id === undefined || receiverLocation.id === undefined) {
    return null;
  }

  return (
    <>
      <MissingOperationsInfo
        hide={
          isLocationView ||
          hideMissingOperationsInfo ||
          receiverNeedsProfileEntities.inflight ||
          (needsAdded && hoursAdded && servedDailyAdded)
        }
        onClose={handleHideMissingInfo}
        onComplete={handleCompleteProfile}
      />
      {isLocationView ? renderBreadcrumbs() : <Typography variant="h5">My Dashboard</Typography>}
      <Box mt={5} display="flex" gap="16px">
        <Box minWidth="max-content">
          {dashboardMenuList.map((item, index) => (
            <Typography style={{ position: 'relative' }} variant="subtitle2" key={item.name} gutterBottom>
              <Box
                component={Link}
                sx={linkSx}
                className={index !== getActiveTab() ? '' : `active`}
                to={item.path}
                key={item.name}
              >
                {item.name}
              </Box>
            </Typography>
          ))}
        </Box>
        <Box sx={{ width: 'calc(100% - 170px)' }}>
          <Switch>
            <Route
              path={[paths[0], paths[1]]}
              exact
              render={() => (
                <LocationProfileTab
                  receiver={receiver}
                  receiverTypes={receiverTypes}
                  receiverLocation={receiverLocation}
                  staff={staff}
                  handleUpdateReceiverLocation={handleUpdateReceiverLocation}
                  handleUpdateReceiverLocationLogo={handleUpdateReceiverLocationLogo}
                  isAdmin={isAdmin}
                />
              )}
            />
            <Route
              path={paths[2]}
              exact
              render={() => (
                <LocationOperationsTab
                  receiver={receiver}
                  receivers={receivers}
                  receiverLocation={receiverLocation}
                  foodDescriptions={foodDescriptions}
                  handleUpdateReceiverOperations={handleUpdateReceiverOperations}
                />
              )}
            />
            <Route
              path={paths[3]}
              exact
              render={() => (
                <StaffTab
                  receiver={receiver}
                  receiverLocation={receiverLocation}
                  receivers={receivers}
                  deleteReceiverStaff={deleteReceiverStaff}
                  notificationsContext={notificationsContext}
                />
              )}
            />
            <Route path={paths[4]} exact render={() => <FutureRescuesTab receiver={receiver} isAdmin={isAdmin} />} />
            <Route path={paths[5]} exact render={() => <PastRescuesTab receiver={receiver} />} />
            <Route
              path={paths[6]}
              exact
              render={() => (
                <LeaderboardsTab
                  receiverId={receiver.id}
                  fetchReceiverLocationDashboardAggregates={fetchReceiverLocationDashboardAggregates}
                />
              )}
            />
            {canViewLogs && <Route path={paths[8]} exact render={() => <LogsTab receiver={receiver} />} />}
          </Switch>
        </Box>
      </Box>
    </>
  );
};

export default ReceiverDashboard;
